// Lightweight navigation guard injected into lesson iframes.
// Normalizes assessment gating and prevents Knowledge Checks from blocking nav.
export function injectNavGuard(doc) {
  try {
    const w = doc.defaultView || window;
    if (!w || w.__navGuardInstalled) return;
    w.__navGuardInstalled = true;

    const pages = Array.from(doc.querySelectorAll('.lesson-page'));
    if (!pages.length) return;

    // Normalize answer map on window
    if (!w.answeredAssessments || typeof w.answeredAssessments !== 'object') {
      w.answeredAssessments = {};
    }

    const labelForPage = (el) => (el?.dataset.page || '').trim().toLowerCase();
    const assessmentKeyForLabel = (label) => {
      const m = /assessment\s+q(\d+)/i.exec(label || '');
      return m ? `aq${m[1]}` : null;
    };
    const keyForPage = (el) => {
      const label = labelForPage(el);
      return assessmentKeyForLabel(label) || label;
    };
    const ensureResultsRendered = () => {
      try {
        const active = pages.find(p => p.classList.contains('active'));
        if (!active) return;
        const tag = keyForPage(active);
        if (tag === 'results' && typeof w.showResults === 'function') {
          w.showResults();
        }
      } catch (e) {
        // swallow
      }
    };

    const isAssessmentAnswered = (pageEl) => {
      if (!pageEl) return false;
      const label = labelForPage(pageEl);
      const key = keyForPage(pageEl);
      if (w.answeredAssessments[key] || w.answeredAssessments[label]) return true;

      // Fallback signal from template UI state.
      return !!pageEl.querySelector(
        '.option.selected, .quiz-option.selected, .option.correct, .quiz-option.correct, input[type="radio"]:checked'
      );
    };

    // Wrap an existing navigate if present
    const originalNavigate = w.navigate || w.navigatePage || w.handleNav;
    const guardNavigate = function (dir) {
      try {
        const currentIdx = pages.findIndex(p => p.classList.contains('active'));
        const nextIdx = currentIdx + dir;
        if (nextIdx < 0 || nextIdx >= pages.length) return;

        const currentLabel = labelForPage(pages[currentIdx]);

        // Try the template's own navigate first; if it fails to move the page, fall back
        if (typeof originalNavigate === 'function') {
          try {
            const beforeIdx = currentIdx;
            originalNavigate.call(w, dir);
            const afterIdx = pages.findIndex(p => p.classList.contains('active'));
            if (afterIdx !== -1 && afterIdx !== beforeIdx) {
              ensureResultsRendered();
              return; // template navigation succeeded
            }
            console.warn('Template navigate did not advance, using guard fallback');
          } catch (err) {
            console.warn('Template navigate failed, using guard fallback', err);
          }
        }

        // Only gate on assessment pages for fallback navigation.
        if (dir === 1 && currentLabel.startsWith('assessment q') && !isAssessmentAnswered(pages[currentIdx])) {
          const alertEl = doc.getElementById('navMessage') || null;
          if (alertEl) {
            alertEl.textContent = 'Please select an option before continuing.';
            alertEl.classList.add('visible');
            setTimeout(() => alertEl.classList.remove('visible'), 2000);
          }
          return;
        }

        // Fallback: advance using DOM only and keep template globals in sync
        pages[currentIdx]?.classList.remove('active');
        pages[nextIdx]?.classList.add('active');
        if (typeof w.currentPage === 'number') {
          w.currentPage = nextIdx;
        }
        if (Array.isArray(w.pages)) {
          w.pages = pages;
        }
        if (typeof w.navLocked === 'boolean') {
          w.navLocked = false;
        }

        // Reset scroll
        const scroller = doc.querySelector('.content-area') || doc.body;
        if (scroller) scroller.scrollTop = 0;

        // Update progress if elements exist
        const progressText = doc.getElementById('progressText') || doc.getElementById('progText');
        const progressFill = doc.getElementById('progressFill') || doc.getElementById('progFill');
        if (progressText) progressText.textContent = `${nextIdx + 1}/${pages.length}`;
        if (progressFill) progressFill.style.width = `${((nextIdx + 1) / pages.length) * 100}%`;

        // Trigger results if needed
        const tag = keyForPage(pages[nextIdx]);
        if (tag === 'results' && typeof w.showResults === 'function') {
          w.showResults();
        }

        // Call template updateUI if present to refresh nav/button states
        if (typeof w.updateUI === 'function') {
          try { w.updateUI(); } catch (e) { /* swallow */ }
        }
        ensureResultsRendered();
      } catch (err) {
        console.error('NavGuard navigate error', err);
      }
    };

    // Replace the global navigate handlers if found
    if (w.navigate) w.navigate = guardNavigate;
    else if (w.navigatePage) w.navigatePage = guardNavigate;
    else w.navigate = guardNavigate;

    // Patch assessment handlers to record with normalized keys
    const wrapHandler = (fnName) => {
      const fn = w[fnName];
      if (typeof fn !== 'function') return;
      w[fnName] = function (...args) {
        try {
          const btn = args[0];
          // Try to infer page from button's closest lesson-page
          const pageEl = btn?.closest?.('.lesson-page');
          const label = labelForPage(pageEl);
          const aqKey = assessmentKeyForLabel(label);
          if (aqKey) {
            // Write both key styles for compatibility with template-specific logic.
            w.answeredAssessments[aqKey] = true;
            w.answeredAssessments[label] = true;
          }
        } catch (e) {
          // swallow
        }
        return fn.apply(this, args);
      };
    };

    wrapHandler('handleAssessment');
    wrapHandler('selectAssessment');
    wrapHandler('checkAssessment');
    wrapHandler('verifyKC'); // KCs should not gate nav

    // Ensure Next button is visible on KCs
    doc.querySelectorAll('.lesson-page').forEach((page) => {
      const tag = keyForPage(page);
      if (tag.startsWith('knowledge check')) {
        const nextBtn = doc.getElementById('nextBtn');
        if (nextBtn) nextBtn.style.visibility = 'visible';
      }
    });

    ['DOMContentLoaded', 'load', 'click', 'keyup'].forEach((evt) => {
      try {
        w.addEventListener(evt, ensureResultsRendered, { passive: true });
      } catch (e) {
        // swallow
      }
    });
    ensureResultsRendered();
  } catch (err) {
    console.error('NavGuard inject error', err);
  }
}
